import { createElement, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { USE_MOCKS, apiDelete, apiGet, apiGetList, apiPut } from './client';

/**
 * Marketing CMS seam (PHASE2 D10; extended to cover ALL public marketing copy).
 * Public read of the platform marketing content map, and the platform-admin /
 * reseller editor list/save. In mock mode the public read returns `{}` so the
 * marketing pages fall back to their built-in defaults.
 *
 * Two section shapes: SINGLE (a flat map of string fields) and LIST (repeated
 * content — feature grids, badge lists, blog posts, FAQ rows). See `cmsText` /
 * `cmsList` below.
 */

/** A SINGLE section's content is a flat map of string fields (e.g. hero = {eyebrow, headline, subhead}). */
export type CmsSectionValue = Record<string, string>;
/** A LIST section's content: repeated items, each a flat map of string / string[] fields. */
export type CmsListItem = Record<string, string | string[]>;
export type CmsListSectionValue = { items: CmsListItem[] };
export type CmsMap = Record<string, CmsSectionValue | CmsListSectionValue>;

/**
 * Public content map `{ sectionKey: { field: value } }` — host-aware (Decision #21),
 * mirroring listPricingPagePackages(): pass the browser's own hostname so a
 * reseller's domain gets the platform content merged with that reseller's own
 * overrides; the API host differs from window.location.host.
 */
export async function getCms(): Promise<CmsMap> {
  if (USE_MOCKS) return {};
  try {
    return await apiGet<CmsMap>(`/cms?host=${encodeURIComponent(typeof window !== 'undefined' ? window.location.host : '')}`);
  } catch {
    return {};
  }
}

export function useCms() {
  return useQuery({ queryKey: ['cms'], queryFn: getCms, staleTime: 60_000 });
}

/** Read a single field with a hardcoded fallback — the pattern every marketing page uses. */
export function cmsText(cms: CmsMap | undefined, section: string, field: string, fallback: string): string {
  const sectionValue = cms?.[section] as Record<string, unknown> | undefined;
  const v = sectionValue?.[field];
  return typeof v === 'string' && v.trim() !== '' ? v : fallback;
}

/**
 * Read a LIST section's items, falling back to `fallbackItems` when the section
 * is absent, empty, or malformed (any shape mismatch — never throws). Per-item
 * fields are NOT individually defaulted here: a CMS item that's missing a field
 * yields `undefined` for that key, so callers should guard (`item.body ?? ''`)
 * rather than assume every fallback field is present.
 */
export function cmsList<T extends Record<string, string | string[]>>(
  cms: CmsMap | undefined,
  section: string,
  fallbackItems: T[],
): T[] {
  const sectionValue = cms?.[section];
  if (
    sectionValue &&
    typeof sectionValue === 'object' &&
    !Array.isArray(sectionValue) &&
    'items' in sectionValue &&
    Array.isArray((sectionValue as CmsListSectionValue).items) &&
    (sectionValue as CmsListSectionValue).items.length > 0
  ) {
    return (sectionValue as CmsListSectionValue).items as unknown as T[];
  }
  return fallbackItems;
}

/**
 * Render `**bold**` spans in a plain-text CMS field as `<strong>` elements — the
 * only "rich" markup the CMS supports. Built from React elements only (no HTML
 * parsing, no `dangerouslySetInnerHTML`), so an untrusted CMS value is exactly
 * as XSS-safe as any other plain string field.
 */
export function cmsRich(text: string): ReactNode {
  const parts = text.split('**');
  if (parts.length === 1) return text;
  return parts.map((part, i) =>
    i % 2 === 1 ? createElement('strong', { key: i, className: 'font-medium text-zinc-900 dark:text-zinc-100' }, part) : part,
  );
}

// ── Admin editor ────────────────────────────────────────────────────────────
/** A section's stored value — SINGLE (flat string map) or LIST (`{ items }`). */
export type CmsSectionShape = CmsSectionValue | CmsListSectionValue;

export interface AdminCmsSection {
  sectionKey: string;
  value: CmsSectionShape;
  updatedAt: string | null;
}

export function useAdminCms() {
  return useQuery({ queryKey: ['admin-cms'], queryFn: () => apiGetList<AdminCmsSection>('/admin/cms') });
}

export function saveCmsSection(sectionKey: string, value: CmsSectionShape) {
  return apiPut(`/admin/cms/${sectionKey}`, { value });
}

/** "Reset to default" — deletes the row so the section falls back to the page's own built-in copy. */
export function clearCmsSection(sectionKey: string) {
  return apiDelete<{ ok: true }>(`/admin/cms/${sectionKey}`);
}

// ── Reseller editor (own-scoped, whitelisted sections — see RESELLER_CMS_KEYS on the API) ──
export function useResellerCms() {
  return useQuery({ queryKey: ['reseller-cms'], queryFn: () => apiGetList<AdminCmsSection>('/reseller/cms') });
}

export function saveResellerCmsSection(sectionKey: string, value: CmsSectionShape) {
  return apiPut(`/reseller/cms/${sectionKey}`, { value });
}

/** Reseller "Reset to default" — deletes its own override row for a whitelisted section. */
export function clearResellerCmsSection(sectionKey: string) {
  return apiDelete<{ ok: true }>(`/reseller/cms/${sectionKey}`);
}
